import { useEffect, useRef, useState } from "react";
import ToolkitBoard, { ToolkitBoardProps } from "./ToolkitBoard";
import DiscussContainer from "src/pods/ideas/components/cards/screen/DiscussContainer";
import WatchVideoContainer from "src/pods/ideas/components/cards/screen/WatchVideoContainer";
import { Lesson, Slide } from "src/pods/ideas/data/types";
import { Question } from "src/pods/pairshare/types";
import { displayOpen, displayClose } from "src/pods/realtime";
import { dispatchToolkitReduxAction } from "src/state/toolkitReduxStore";

type Video = {
  watch: Slide;
  lesson: Lesson;
};

export default function BoardContainer({ board }: ToolkitBoardProps) {
  const [question, setQuestion] = useState<Question | null>(null);
  const [video, setVideo] = useState<Video | null>(null);
  const lastScrollPositionRef = useRef<number>();
  const previousQuestionRef = useRef<Question | null>(null);
  const previousVideoRef = useRef<Video | null>(null);

  useEffect(() => {
    const willDisplayVideo = !previousVideoRef.current && video;
    const willDisplayQuestion = !previousQuestionRef.current && question;

    if (willDisplayVideo || willDisplayQuestion) {
      lastScrollPositionRef.current = window.scrollY;
    }

    if (lastScrollPositionRef.current !== undefined) {
      window.scrollTo(0, lastScrollPositionRef.current);
    }

    previousQuestionRef.current = question;
    previousVideoRef.current = video;
  }, [question, video]);

  const handleDisplayOpen: typeof displayOpen = (...args) => {
    const [path, action] = args as Parameters<typeof displayOpen>;
    const displayOpenAction = displayOpen(path, action);
    dispatchToolkitReduxAction(displayOpenAction);
    return displayOpenAction;
  };

  const handleDisplayClose: typeof displayClose = () => {
    const displayCloseAction = displayClose();
    dispatchToolkitReduxAction(displayCloseAction);
    return displayCloseAction;
  };

  if (question) {
    return (
      <DiscussContainer
        displayOpen={handleDisplayOpen}
        displayClose={handleDisplayClose}
        onBack={() => setQuestion(null)}
        question={question}
      />
    );
  }

  if (video) {
    return (
      <WatchVideoContainer
        displayOpen={handleDisplayOpen}
        displayClose={handleDisplayClose}
        onBack={() => setVideo(null)}
        watch={video.watch}
        lesson={video.lesson}
      />
    );
  }

  return (
    <ToolkitBoard
      board={board}
      displayDiscussCard={setQuestion}
      displayVideoCard={(watch, lesson) =>
        setVideo({
          watch,
          lesson,
        })
      }
    />
  );
}
